// ============================================================
// AttackerBodyMap - SVG body silhouette showing attacker
// offensive status. Display-only (not interactive for called shots).
// Shows trait/talent icons and status badges.
// Supports all body types: humanoid, vehicle, amorphous, horde.
// ============================================================
import { memo } from 'react';
import { deriveAttackerStatus } from '../engine/attackerStatus';
import { TALENT_CATALOG } from '../engine/character/talentCatalog';
import { TRAIT_CATALOG } from '../engine/character/traitCatalog';
import Badge from './Badge';
import BadgeRow from './BadgeRow';
import type { Character, AttackerCombatStats } from '../engine/character/types';
import type { Target } from '../types';
import type { Impact } from '../engine/impacts';
import { buildTargetTraitItems } from './BodyMap.jsx';
import BodyPlate from './plate/BodyPlate';
import { figureFor, NOBODY, type FigureTarget } from '../engine/plate/figureFor';
import PlateThumb from './plate/PlateThumb';
import PlateCrowd from './plate/PlateCrowd';
import styles from '../styles/AttackerPanel.module.css';
import bodyStyles from '../styles/BodyMap.module.css';

interface Props {
  slot: { weaponType: string; ammo?: { current: number; max: number }; isEmpty: boolean } | null;
  attackerCharStats: AttackerCombatStats | null;
  attackToggles: Record<string, boolean>;
  hatred: boolean;
  frenzied?: boolean;
  sourceCharacter: Character | null;
}

const AMMO_COLORS: Record<string, string> = {
  ok: '#22c55e',
  low: '#f59e0b',
  empty: '#ef4444',
  melee: '#6b7280',
};

const MELEE_ONLY_TALENTS = ['swift_attack', 'lightning_attack', 'crushing_blow', 'counter_attack', 'wall_of_steel'];
const RANGED_ONLY_TALENTS = ['mighty_shot'];

/** Is this talent in play with the weapon in hand? A melee-only talent needs a melee weapon, and back. */
function talentActive(talentId: string, isMelee: boolean): boolean {
  if (MELEE_ONLY_TALENTS.includes(talentId)) return isMelee;
  if (RANGED_ONLY_TALENTS.includes(talentId)) return !isMelee;
  return true;
}

/** Talents the character owns, dimmed when the selected weapon is wrong for them. */
function attackerTalentItems(sourceCharacter: Character | null, isMelee: boolean) {
  if (!sourceCharacter?.talents) return [];
  return sourceCharacter.talents
    .filter((t) => TALENT_CATALOG[t.talentId])
    .map((t) => ({
      talentId: t.talentId,
      name: TALENT_CATALOG[t.talentId].name,
      active: talentActive(t.talentId, isMelee),
    }));
}

function attackerTraitItems(sourceCharacter: Character | null) {
  if (!sourceCharacter?.traits) return [];
  return sourceCharacter.traits
    .map((t) => {
      const def = TRAIT_CATALOG[t.traitId];
      return def ? { traitId: t.traitId, name: def.name } : null;
    })
    .filter(Boolean) as { traitId: string; name: string }[];
}

/**
 * The actor's trait and talent badges, as its row draws them. The traits come from the live projection through
 * `buildTargetTraitItems`, as the target's do, so one combatant shows the same traits on both sides: the flat fields
 * (Fear), what the ground lends, and the sheet traits with no field of their own (Quadruped) - the old list-only
 * builder showed a field-set Fear on one side and not the other (v7.79.0). Talents are the sheet's own; the
 * Astartes-granted ones never appear (the one Astartes badge stands for the package; they are in the Overview).
 */
function attackerBadges(sourceCharacter: Character | null, isMelee: boolean, target?: Target | null) {
  const traits: { label: string; value?: number }[] = target
    ? buildTargetTraitItems(target, sourceCharacter)
    : attackerTraitItems(sourceCharacter).map((t) => ({ label: t.name }));
  return {
    traits: traits.map((t, i) => (
      <Badge key={`${t.label}-${i}`} variant="trait" label={t.label} value={t.value} size={28} />
    )),
    talents: attackerTalentItems(sourceCharacter, isMelee).map((t) => (
      <Badge key={t.talentId} variant="talent" label={t.name} active={t.active} size={28} />
    )),
  };
}

/**
 * The actor's traits, then its own talents, as one row under the health bar (plan plate-unboxed); nothing when it
 * has neither. Each group keeps its test id, so the row reads as the rail did.
 */
export function AttackerTraitRow({
  sourceCharacter,
  isMelee,
  target,
}: {
  sourceCharacter: Character | null;
  isMelee: boolean;
  target?: Target | null;
}) {
  const { traits, talents } = attackerBadges(sourceCharacter, isMelee, target);
  if (traits.length === 0 && talents.length === 0) return null;
  return (
    <BadgeRow
      label="Traits and talents"
      full={
        <>
          {traits}
          {talents}
        </>
      }
    >
      {traits.length > 0 && (
        <div data-row-group="" data-testid="attacker-trait-badges">
          {traits}
        </div>
      )}
      {talents.length > 0 && (
        <div data-row-group="" data-testid="attacker-talent-badges">
          {talents}
        </div>
      )}
    </BadgeRow>
  );
}

// A bonus chip's colours by where the bonus comes from; anything else is an attack toggle.
const BONUS_COLOURS: Record<string, { color: string; borderColor: string }> = {
  talent: { color: '#fbbf24', borderColor: '#d97706' },
  hatred: { color: '#ef4444', borderColor: '#dc2626' },
};
const TOGGLE_COLOURS = { color: '#60a5fa', borderColor: '#3b82f6' };

/** Ammo and active-bonus chips - attack context, not body state. */
export function AttackerStatusChips({
  slot,
  attackerCharStats,
  attackToggles,
  hatred,
  frenzied = false,
  sourceCharacter,
}: Props) {
  const status = deriveAttackerStatus(slot, attackerCharStats, attackToggles, hatred, sourceCharacter, frenzied);
  const hasAmmo = !!slot && !slot.isEmpty && status.ammoStatus !== 'melee';
  if (!hasAmmo && status.activeBonuses.length === 0) return null;
  return (
    <div className={bodyStyles.statusChips} data-testid="attacker-status-chips">
      {hasAmmo && <AmmoChip slot={slot} ammoStatus={status.ammoStatus} />}
      {status.activeBonuses.map((b) => (
        <span key={b.label} className={bodyStyles.statusChip} style={BONUS_COLOURS[b.source] ?? TOGGLE_COLOURS}>
          {b.label}
        </span>
      ))}
    </div>
  );
}

/** EMPTY, LOW AMMO, or the rounds left, coloured by how worried to be. */
function AmmoChip({ slot, ammoStatus }: { slot: NonNullable<Props['slot']>; ammoStatus: string }) {
  const colour = AMMO_COLORS[ammoStatus];
  const text: Record<string, string> = { empty: 'EMPTY', low: 'LOW AMMO' };
  return (
    <span className={bodyStyles.statusChip} style={{ color: colour, borderColor: colour }}>
      {text[ammoStatus] ?? `${slot.ammo?.current}/${slot.ammo?.max}`}
    </span>
  );
}

interface AttackerBodyMapProps extends Props {
  /** `fill` renders ONLY the silhouette, sized to its container. */
  variant?: 'standalone' | 'fill';
  /**
   * The actor as a live projection and their own sheet (a statblock's too), whose body the plain view draws in
   * phosphor (plan body-map P3, P8); without a projection it reads the body off the sheet.
   */
  actorTarget?: Target | null;
  figureSheet?: Character | null;
  /** What just happened to the actor (engine/impacts): the plain plate plays it too. */
  impact?: Impact | null;
}

type SilhouetteProps = Pick<
  AttackerBodyMapProps,
  'sourceCharacter' | 'variant' | 'actorTarget' | 'figureSheet' | 'impact'
>;

/**
 * A sheet read as a body, for the view with no live projection: its body type (the amorphous trait makes it the mass,
 * as the old silhouette did), its horde, its vehicle kind and its Figure pin; Astartes from its trait.
 */
function sheetBody(c: Character): FigureTarget {
  const has = (id: string) => (c.traits ?? []).some((t) => t.traitId === id);
  return {
    astartes: has('astartes'),
    machineAP: 0,
    bodyType: c.bodyType ?? (has('amorphous') ? 'amorphous' : undefined),
    horde: c.horde,
    vehicleKind: c.vehicleKind,
    figure: c.figure,
  };
}

const KIND_LABEL = { person: 'humanoid', vehicle: 'vehicle', goo: 'amorphous', object: 'object' } as const;

/**
 * The actor plain, in phosphor (plan body-map P3, P8): a person's plain plate, a crowd's plate crowd, a vehicle's or
 * the mass's thumbnail - the figure alone, with no numbers.
 */
function AttackerSilhouette({
  sourceCharacter,
  variant = 'standalone',
  actorTarget,
  figureSheet,
  impact,
}: SilhouetteProps) {
  const className = variant === 'fill' ? bodyStyles.svgFill : bodyStyles.svg;
  const sheet = figureSheet ?? sourceCharacter;
  const body = actorTarget ? figureFor(actorTarget, sheet) : sheet ? figureFor(sheetBody(sheet), sheet) : NOBODY;
  const label = `Attacker ${KIND_LABEL[body.kind]} body status map`;
  if (actorTarget && body.crowd) {
    return (
      <PlateCrowd
        body={body}
        target={actorTarget}
        side="actor"
        fill={variant === 'fill'}
        ariaLabel="Attacker horde body map"
        impact={impact}
      />
    );
  }
  if (actorTarget && body.kind === 'person') {
    return (
      <BodyPlate
        plain
        side="actor"
        figure={body.figure}
        target={actorTarget}
        sheet={sheet}
        fill={variant === 'fill'}
        ariaLabel={label}
        impact={impact}
      />
    );
  }
  return <PlateThumb body={body} colour="var(--actor)" ariaLabel={label} className={className} />;
}

function AttackerBodyMap({
  slot,
  attackerCharStats,
  attackToggles,
  hatred,
  frenzied = false,
  sourceCharacter,
  variant = 'standalone',
  actorTarget,
  figureSheet,
  impact,
}: AttackerBodyMapProps) {
  if (variant === 'fill') {
    return (
      <AttackerSilhouette
        sourceCharacter={sourceCharacter}
        variant="fill"
        actorTarget={actorTarget}
        figureSheet={figureSheet}
        impact={impact}
      />
    );
  }

  return (
    <div className={bodyStyles.mapColumn}>
      <div className={bodyStyles.mapRow}>
        <AttackerSilhouette sourceCharacter={sourceCharacter} />
      </div>
      <AttackerStatusChips
        slot={slot}
        attackerCharStats={attackerCharStats}
        attackToggles={attackToggles}
        hatred={hatred}
        frenzied={frenzied}
        sourceCharacter={sourceCharacter}
      />
      {!sourceCharacter && <div className={styles.overviewEmpty}>Load a character to see talents and traits.</div>}
    </div>
  );
}

export default memo(AttackerBodyMap);
